import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { api } from '~/lib/api-client';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/packs/new')({
  head: () =>
    buildHead({
      title: 'Upload Sample Pack',
      description: 'Submit a sample pack with 8+ stems. Auto-matched to upcoming battles.',
      path: '/packs/new',
      noindex: true,
    }),
  component: NewPack,
});

type Genre = { id: string; slug: string; name: string; stemTypes: string[] | null };

type StagedSample = {
  stemType: string;
  file: File;
};

type UploadHandle = {
  stemType: string;
  uploadUrl: string;
  publicUrl: string;
  key: string;
};

// Canonical list (keep in sync with STEM_TYPES on the API side in
// src/routes/user-packs.ts). Used as the fallback when a genre has no
// stemTypes set. Most of the time we narrow to the genre's own list.
const ALL_STEM_TYPES = [
  'kick',
  'snare',
  'hihat',
  'openhat',
  'clap',
  'perc',
  'fx',
  '808',
  'bass',
  'lead',
  'pad',
  'vocal',
  'zap',
  'screech',
  'reverse',
  'cowbell',
];

// Infer a stem type from a filename by matching any allowed stem as a
// substring. Case-insensitive, punctuation-tolerant. Falls back to the
// first allowed stem if nothing in the filename matches.
function inferStemType(filename: string, allowed: readonly string[]): string {
  const hay = filename.toLowerCase().replace(/[^a-z0-9]+/g, ' ');
  const match = allowed.find((s) => hay.includes(s.toLowerCase()));
  return match ?? allowed[0] ?? 'kick';
}

// Reusable upload form. Calls onDone() when a pack is finalized - the caller
// decides whether to navigate (standalone /packs/new) or refresh (Studio tab).
export function UploadPackForm({ onDone }: { onDone: () => void }) {
  const [genres, setGenres] = useState<Genre[]>([]);
  const [genreId, setGenreId] = useState('');
  const [name, setName] = useState('');
  const [samples, setSamples] = useState<StagedSample[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copyrightAttested, setCopyrightAttested] = useState(false);

  useEffect(() => {
    api<{ items: Genre[] }>('/genres').then((r) => {
      setGenres(r.items);
      if (r.items[0]) setGenreId(r.items[0].id);
    });
  }, []);

  // When the genre changes, re-map any staged samples whose stemType isn't
  // valid for the new genre. Filename inference picks the best new slot.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only runs on genreId change
  useEffect(() => {
    if (samples.length === 0) return;
    const g = genres.find((gr) => gr.id === genreId);
    const allowed = g?.stemTypes && g.stemTypes.length > 0 ? g.stemTypes : ALL_STEM_TYPES;
    setSamples((prev) =>
      prev.map((s) =>
        allowed.includes(s.stemType) ? s : { ...s, stemType: inferStemType(s.file.name, allowed) },
      ),
    );
  }, [genreId]);

  const selectedGenre = genres.find((g) => g.id === genreId);
  const allowedStems =
    selectedGenre?.stemTypes && selectedGenre.stemTypes.length > 0
      ? selectedGenre.stemTypes
      : ALL_STEM_TYPES;

  function addFiles(fl: FileList | null) {
    if (!fl) return;
    const next: StagedSample[] = [];
    for (let i = 0; i < fl.length; i++) {
      const file = fl.item(i);
      if (!file) continue;
      next.push({ stemType: inferStemType(file.name, allowedStems), file });
    }
    setSamples((s) => [...s, ...next].slice(0, 32));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (samples.length === 0 || !genreId || !name) return;
    setBusy(true);
    setErr(null);
    try {
      // 1. Ask the API for presigned PUT URLs, one per staged sample.
      const presign = await api<{
        packStagingId: string;
        samples: UploadHandle[];
      }>('/user-packs/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          samples: samples.map((s) => ({
            stemType: s.stemType,
            filename: s.file.name,
            contentType: s.file.type || 'application/octet-stream',
          })),
        }),
      });

      // 2. PUT each file directly to S3.
      await Promise.all(
        presign.samples.map((h, i) => {
          const f = samples[i]?.file;
          if (!f) throw new Error('missing file');
          return fetch(h.uploadUrl, {
            method: 'PUT',
            headers: { 'content-type': f.type || 'application/octet-stream' },
            body: f,
          });
        }),
      );

      // 3. Finalize: create the sample_packs row.
      await api('/user-packs', {
        method: 'POST',
        body: JSON.stringify({
          genreId,
          name,
          copyrightAttested,
          samples: presign.samples.map((h, i) => {
            const src = samples[i];
            if (!src) throw new Error('missing source sample');
            return {
              stemType: h.stemType,
              name: src.file.name,
              url: h.publicUrl,
            };
          }),
        }),
      });

      setName('');
      setSamples([]);
      setCopyrightAttested(false);
      onDone();
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Genre</span>
        <select
          value={genreId}
          onChange={(e) => setGenreId(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2"
        >
          {genres.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Pack name</span>
        <input
          type="text"
          required
          minLength={2}
          maxLength={64}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2"
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block text-muted-foreground">Add audio files</span>
        <input
          type="file"
          multiple
          accept="audio/*"
          onChange={(e) => addFiles(e.target.files)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        {selectedGenre && (
          <span className="mt-2 block text-xs text-muted-foreground">
            {selectedGenre.name} expects{' '}
            <span className="text-foreground">{allowedStems.join(', ')}</span>. Filenames get
            auto-matched; adjust manually below if needed.
          </span>
        )}
      </label>

      {samples.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          {allowedStems.map((stem) => {
            const covered = samples.some((s) => s.stemType === stem);
            return (
              <span
                key={stem}
                className={`rounded-full border px-2 py-0.5 ${
                  covered
                    ? 'border-primary/60 bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {stem}
              </span>
            );
          })}
        </div>
      )}

      <ul className="space-y-2">
        {samples.map((s, i) => (
          <li
            key={`${s.file.name}-${i}`}
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
          >
            <span className="flex-1 truncate">{s.file.name}</span>
            <select
              value={s.stemType}
              onChange={(e) => {
                const v = e.target.value;
                setSamples((prev) => prev.map((p, j) => (j === i ? { ...p, stemType: v } : p)));
              }}
              className="rounded border border-border bg-background px-2 py-1"
            >
              {allowedStems.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSamples((prev) => prev.filter((_, j) => j !== i))}
              className="text-xs text-red-500"
            >
              remove
            </button>
          </li>
        ))}
      </ul>

      {err && <p className="text-sm text-red-500">{err}</p>}

      <label className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3 text-xs leading-relaxed">
        <input
          type="checkbox"
          checked={copyrightAttested}
          onChange={(e) => setCopyrightAttested(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          I confirm that every sample I'm uploading is my own work or is licensed for redistribution
          (CC0, royalty-free, cleared, or equivalent). I understand that uploading copyrighted
          material I don't own may result in removal and account suspension. This confirmation is
          logged with the pack.
        </span>
      </label>

      <button
        type="submit"
        disabled={busy || samples.length === 0 || !name || !genreId || !copyrightAttested}
        title={
          copyrightAttested ? undefined : 'Confirm the copyright notice above to enable upload.'
        }
        className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
      >
        {busy
          ? 'Uploading...'
          : `Upload ${samples.length} sample${samples.length === 1 ? '' : 's'}`}
      </button>
    </form>
  );
}

function NewPack() {
  const { data: session, isLoading } = useSession();
  const navigate = useNavigate();

  if (isLoading) return <section className="p-8">Loading...</section>;
  if (!session) {
    return (
      <section className="mx-auto max-w-md px-4 py-16 text-center">
        <h2 className="text-2xl font-bold">Sign in to upload a pack</h2>
        <a href="/auth/sign-in" className="mt-3 inline-block underline">
          Sign in
        </a>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-2xl px-4 py-10 space-y-5">
      <header>
        <h2 className="text-2xl font-bold">New sample pack</h2>
        <p className="text-muted-foreground text-sm">
          Upload up to 32 stems. They'll be available in any match using the attached genre.
        </p>
      </header>
      <UploadPackForm onDone={() => navigate({ to: '/feed' })} />
    </section>
  );
}
